import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { Shield, Lock, Key, Smartphone, Clock, AlertTriangle, CheckCircle, XCircle, QrCode } from 'lucide-react';

const SecurityManagement = () => {
  const [activeSection, setActiveSection] = useState('2fa');
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adminProfile, setAdminProfile] = useState(null);

  useEffect(() => {
    fetchAdminProfile();
    fetchSessions();
  }, []);

  const fetchAdminProfile = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const baseUrl = API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/admin/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAdminProfile(data);
      }
    } catch (error) {
      console.error('Error fetching admin profile:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const baseUrl = API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/admin/security/sessions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const setup2FA = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const baseUrl = API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/admin/security/2fa/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTwoFactorSetup(data);
      } else {
        alert('Failed to setup 2FA');
      }
    } catch (error) {
      console.error('Error setting up 2FA:', error);
      alert('Error setting up 2FA');
    } finally {
      setLoading(false);
    }
  };

  const verify2FA = async (token) => {
    setLoading(true);
    try {
      const adminToken = localStorage.getItem('adminToken');
      const baseUrl = API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/admin/security/2fa/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token })
      });

      if (response.ok) {
        alert('2FA enabled successfully!');
        setTwoFactorSetup(null);
        fetchAdminProfile();
      } else {
        alert('Invalid verification code');
      }
    } catch (error) {
      console.error('Error verifying 2FA:', error);
      alert('Error verifying 2FA');
    } finally {
      setLoading(false);
    }
  };

  const terminateSession = async (sessionId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Are you sure you want to terminate this session?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const baseUrl = API_BASE_URL;
      const response = await fetch(`${baseUrl}/api/admin/security/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchSessions();
        alert('Session terminated successfully');
      } else {
        alert('Failed to terminate session');
      }
    } catch (error) {
      console.error('Error terminating session:', error);
      alert('Error terminating session');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <Shield className="h-6 w-6 mr-2 text-[#d44100]" />
          Security Management
        </h2>
        <p className="text-sm text-gray-600 mt-1">Manage your account security settings</p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6 border-b">
        <button
          onClick={() => setActiveSection('2fa')}
          className={`px-4 py-2 font-medium ${
            activeSection === '2fa' 
              ? 'border-b-2 border-[#d44100] text-[#d44100]' 
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Smartphone className="h-4 w-4 inline mr-2" />
          Two-Factor Authentication
        </button>
        <button
          onClick={() => setActiveSection('sessions')}
          className={`px-4 py-2 font-medium ${
            activeSection === 'sessions' 
              ? 'border-b-2 border-[#d44100] text-[#d44100]' 
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Clock className="h-4 w-4 inline mr-2" />
          Active Sessions
        </button>
        <button
          onClick={() => setActiveSection('password')}
          className={`px-4 py-2 font-medium ${
            activeSection === 'password' 
              ? 'border-b-2 border-[#d44100] text-[#d44100]' 
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <Lock className="h-4 w-4 inline mr-2" />
          Password Policy
        </button>
      </div>

      {/* 2FA Section */}
      {activeSection === '2fa' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Two-Factor Authentication</h3>
          
          {adminProfile?.twoFactorEnabled ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-green-800 font-medium">2FA is enabled</span>
              </div>
              <p className="text-sm text-green-700 mt-2">
                Your account is protected with two-factor authentication.
              </p>
            </div>
          ) : (
            <div>
              {!twoFactorSetup ? (
                <div>
                  <p className="text-gray-600 mb-4">
                    Enable two-factor authentication to add an extra layer of security to your account.
                  </p>
                  <button
                    onClick={setup2FA}
                    disabled={loading}
                    className="px-6 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700] disabled:opacity-50"
                  >
                    {loading ? 'Setting up...' : 'Setup 2FA'}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800 mb-2">
                      Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                    </p>
                    <img src={twoFactorSetup.qrCode} alt="2FA QR Code" className="mx-auto mb-4" />
                    <p className="text-xs text-blue-600">
                      Or enter this code manually: <code className="bg-white px-2 py-1 rounded">{twoFactorSetup.manualEntryKey}</code>
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enter verification code
                    </label>
                    <input
                      type="text"
                      placeholder="000000"
                      maxLength={6}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100]"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && e.target.value.length === 6) {
                          verify2FA(e.target.value);
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        const code = document.querySelector('input[placeholder="000000"]').value;
                        if (code.length === 6) verify2FA(code);
                      }}
                      className="mt-2 px-6 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700]"
                    >
                      Verify & Enable
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Sessions Section */}
      {activeSection === 'sessions' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Active Sessions</h3>
          
          {sessions.length === 0 ? (
            <p className="text-gray-600">No active sessions found.</p>
          ) : (
            <div className="space-y-3">
              {sessions.map((session) => (
                <div key={session.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        {session.userAgent || 'Unknown Device'}
                      </p>
                      <p className="text-sm text-gray-600">
                        IP: {session.ipAddress || 'Unknown'} • 
                        Last Activity: {new Date(session.lastActivity).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Expires: {new Date(session.expiresAt).toLocaleString()}
                      </p>
                    </div>
                    <button
                      onClick={() => terminateSession(session.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                    >
                      Terminate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Password Policy Section */}
      {activeSection === 'password' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Password Policy</h3>
          
          <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Password Requirements</h4>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                <li>Minimum 8 characters</li>
                <li>At least one uppercase letter</li>
                <li>At least one lowercase letter</li>
                <li>At least one number</li>
                <li>At least one special character</li>
              </ul>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Security Recommendations</h4>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                <li>Change your password every 90 days</li>
                <li>Don't reuse passwords across accounts</li>
                <li>Use a password manager</li>
                <li>Enable 2FA for additional security</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityManagement;

